import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { History, Maximize2, X, Image as ImageIcon } from "lucide-react";
import { apiService } from "../services/apiService";
import { useAuth } from "../context/AuthContext";

const toHttps = (url) =>
  url?.startsWith("gs://")
    ? `https://storage.googleapis.com/${url.substring(5)}`
    : url;

const b64 = (s) => (s ? `data:image/jpeg;base64,${s}` : null);

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-3 text-sm">
    <dt className="text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
    <dd className="font-medium text-gray-900 dark:text-white text-right break-all">
      {children}
    </dd>
  </div>
);

const ResultsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { record, motherInfo, aiResult, capture_id, image } =
    location.state || {};

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageIdx, setImageIdx] = useState(0);

  if (!record) {
    navigate("/");
    return null;
  }

  const captureId = record.capture_id || capture_id || aiResult?.capture_id;
  const type = (record.detection_type || record.jenis_counting || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

  const count =
    record.jumlah_pills ??
    record.pill_count ??
    aiResult?.pill_count ??
    aiResult?.tablet_count ??
    aiResult?.capsule_count ??
    0;
  const unit = /blister|calcium|tablet/.test(type)
    ? "tablet"
    : /ttd|capsule/.test(type)
      ? "kapsul"
      : "pil";
  const next = /ttd|capsule/.test(type)
    ? ["/ttd-detection", "Pindai Kapsul TTD Lagi"]
    : null;

  // Overlay YOLO first, raw photo after; the proxy covers private buckets (403).
  const candidates = [
    location.state?.annotatedPreviewUrl,
    record.yolo_image,
    aiResult?.yolo_image,
    b64(record.annotated_image_base64),
    b64(aiResult?.annotated_image_base64),
    b64(aiResult?.annotatedImageBase64),
    image,
    location.state?.previewUrl,
    record.image_url,
    record.preview_url,
  ]
    .filter((u) => typeof u === "string" && u)
    .map(toHttps);
  if (captureId) {
    const t = type || "mms_bottle";
    candidates.push(
      apiService.getCaptureImageUrl(captureId, t, true),
      apiService.getCaptureImageUrl(captureId, t)
    );
  }
  const attachmentImage = [...new Set(candidates)][imageIdx] || null;
  const onImageError = () => setImageIdx((i) => i + 1);

  const dateValue = record.tanggal_submit || record.timestamp;
  const date = dateValue
    ? new Date(dateValue).toLocaleString("id-ID", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-";

  // A human-corrected count is "Hitung Manual"; otherwise show the model that counted it.
  const method =
    record.jumlah_pills_koreksi != null
      ? "Hitung Manual"
      : record.model_version ||
        record.ai_metadata?.model_version ||
        aiResult?.ai_metadata?.model_version ||
        "-";

  const aiCount =
    aiResult?.pill_count ?? aiResult?.tablet_count ?? aiResult?.capsule_count;
  const motherName = record.nama_ibu || motherInfo?.mother_name;
  const barcode = record.barcode_id || record.id_barcode || motherInfo?.barcode_id;
  const petugas = user?.name || user?.email;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-16">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Detail Hasil Pindai
          </h1>
          <button
            onClick={() => navigate("/history")}
            className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center"
          >
            <History className="h-4 w-4 mr-1" />
            Riwayat
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <section className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {attachmentImage ? (
            <button
              type="button"
              onClick={() => setIsImageModalOpen(true)}
              aria-label="Perbesar"
              className="relative block w-full bg-gray-950"
            >
              <img
                src={attachmentImage}
                alt="Foto Hasil Scan Obat"
                onError={onImageError}
                className="w-full max-h-[420px] object-contain"
              />
              <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2.5 py-1 rounded-full flex items-center">
                <Maximize2 className="h-3.5 w-3.5 mr-1" />
                Perbesar
              </span>
            </button>
          ) : (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              <ImageIcon className="h-8 w-8 mx-auto mb-2" />
              Foto tidak tersedia
            </div>
          )}
          <div className="p-5">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Hasil Counting
            </p>
            <p className="mt-1 text-gray-900 dark:text-white">
              <span className="text-5xl font-extrabold">{count}</span>
              <span className="ml-2 text-lg text-gray-500 dark:text-gray-400">
                {unit}
              </span>
            </p>
          </div>
        </section>

        <dl className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 px-5 divide-y divide-gray-100 dark:divide-gray-700">
          {motherName && <Row label="Nama ibu">{motherName}</Row>}
          {record.id_ibu && <Row label="ID ibu">{record.id_ibu}</Row>}
          {barcode && <Row label="Barcode">{barcode}</Row>}
          {petugas && <Row label="Petugas">{petugas}</Row>}
          <Row label="Waktu">{date}</Row>
          <Row label="Metode">{method}</Row>
          {aiCount != null && (
            <Row label="Prediksi AI">
              {aiCount} {unit}
              {aiResult.confidence !== undefined &&
                ` (${Math.round(aiResult.confidence * 100)}%)`}
            </Row>
          )}
          {captureId && <Row label="Capture ID">{captureId}</Row>}
        </dl>

        {next && (
          <button
            onClick={() => navigate(next[0])}
            className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          >
            {next[1]}
          </button>
        )}
      </div>

      {isImageModalOpen && attachmentImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsImageModalOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
            title="Tutup"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={attachmentImage}
            alt="Foto Hasil Scan Fullscreen"
            onError={onImageError}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[85vh] object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default ResultsScreen;
